export default function HoumaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Houma &amp; Bayou Region Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Houma's travel patterns are unlike any other Louisiana city — driven primarily by offshore rotational work, maritime contracts, and Gulf Coast industry. Understanding how Bayou Region travelers actually move helps us anticipate the passport urgencies that arise here.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Offshore Rotational Work — North Sea &amp; West Africa</h3>
                <p className="text-gray-400 text-xs">Terrebonne Parish offshore workforce</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Houma is home to one of the largest concentrations of offshore oil and gas workers in the world. Workers rotate out to platforms in the Gulf of Mexico, but international contracts frequently send them to the North Sea (Aberdeen, Bergen, Rotterdam), West Africa (Nigeria, Angola, Equatorial Guinea), and Southeast Asia (Malaysia, Singapore, Brunei). Contract mobilization timelines can be as short as 48–72 hours from notification to departure.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> International offshore contracts typically require a passport valid for at least 6 months beyond the contract end date. Do not wait for contract award — renew proactively if your passport expires within 12 months.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Gulf Cruises &amp; New Orleans Departures</h3>
                <p className="text-gray-400 text-xs">~60 miles to Port of New Orleans</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Port of New Orleans is roughly 60 miles from Houma — an easy drive for Terrebonne Parish families booking Caribbean and Bahamas cruises. The South Louisiana cruise market is strong, with seasonal sailings and holiday departure windows that book quickly. Houma and Thibodaux families often book cruises during promotion windows with 3–6 week lead times, then discover expired passports in the family. Last-minute bookings for New Year's, Mardi Gras, and summer holiday sailings are especially common urgency triggers.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Even Bahamas cruises departing from New Orleans may require passports if any itinerary port is outside U.S. territorial waters or if you miss the ship and need to fly home. Verify before booking.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-anchor-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Maritime Industry &amp; International Voyages</h3>
                <p className="text-gray-400 text-xs">Houma's marine fabrication &amp; vessel fleet</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Terrebonne Parish has a substantial marine construction and boat fabrication industry. Marine welders, vessel operators, dive supervisors, and ROV technicians may need to board vessels in foreign ports — Rotterdam, Gibraltar, Singapore, Busan. Crew changes, equipment delivery, and vessel repositioning create passport requirements with very short timelines. A mariner who missed a crew change due to an expired passport faces significant financial and contractual penalties.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Maritime workers should maintain a TWIC card AND a valid passport. Some foreign ports require both for vessel boarding. Renew your passport when it hits 12 months remaining — don't wait for a contract to force the issue.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Nicholls State University &amp; Study Abroad</h3>
                <p className="text-gray-400 text-xs">Thibodaux — ~15 miles from Houma</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Nicholls State University in Thibodaux — just 15 miles from Houma — sends students on culinary arts exchanges to France, marine biology programs in the Caribbean, and business exchanges in Latin America. First-time passport applicants from the Bayou Region often do not realize how long standard processing takes until after acceptance. We guide NSU students through the first-time passport process, including acceptance clerk visits in Terrebonne and Lafourche Parishes, so they make their program deadlines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> NSU culinary arts students going to France on exchange programs should apply as soon as they receive acceptance — not after orientation. Standard processing often can't accommodate spring semester program deadlines.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-plane-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">MSY &amp; HOU — Airport Access for Houma Travelers</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Louis Armstrong New Orleans International Airport (MSY) is approximately 60 miles from Houma — the primary gateway for international travel. George Bush Intercontinental (IAH) in Houston is roughly 300 miles away. We process passports for Houma-area travelers for every major departure hub, ensuring your documents are ready before your offshore mobilization or international flight.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}